/*
 * Auth context — the app's session source of truth.
 *
 * Owns: the bearer token (delegated to `tokenStore`), the decoded active brand
 * + role + super-admin flag (from the JWT claims), the current user, and the
 * caller's memberships. Exposes the auth *flows* (login / switch-brand /
 * change-password / provision user / create-brand / logout) and a `can()`
 * affordance helper bound to the active role.
 *
 * Admin-provisioned model (no self-registration):
 *   - login     → brand-scoped token, OR a principal-only super-admin token
 *                 (no membership).
 *   - change-password → requires the current password; re-mints a fresh token.
 *   - create-user (active-brand admin) / create-brand (super-admin) →
 *                 provision other principals; the CALLER's own token is unchanged.
 *   - switch-brand → a fresh brand-scoped token.
 *
 * On any token change we refresh `user` + `memberships`; a 401 anywhere clears
 * the token (via the API middleware), which this context observes and treats as
 * a logout.
 */

import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import type {
  MembershipResponse,
  Role,
  UserResponse,
} from "../api/client";
import type { CreateBrandResponse, ProvisionUserRequest } from "../api/client";
import {
  login as apiLogin,
  switchBrand as apiSwitchBrand,
  createBrand as apiCreateBrand,
  createUser as apiCreateUser,
  changePassword as apiChangePassword,
  getMe,
  getMyMemberships,
} from "../api/endpoints";
import {
  getToken,
  setToken,
  clearToken,
  subscribe,
} from "./tokenStore";
import { decodeToken } from "./jwt";
import { can as canFn, type Capability } from "./roles";

export interface AuthContextValue {
  /** Current bearer token, or null when signed out. */
  token: string | null;
  /** True while bootstrapping the session (initial /me fetch). */
  loading: boolean;
  /** The signed-in user, once loaded. */
  user: UserResponse | null;
  /** Every brand the user belongs to (id + role). */
  memberships: MembershipResponse[];
  /** Brand the active token is scoped to; null for principal-only tokens. */
  activeBrandId: string | null;
  /** Role within the active brand; null if principal-only. */
  role: Role | null;
  /** Convenience: signed in with a brand-scoped token. */
  isAuthenticated: boolean;
  /**
   * Convenience: the session is scoped to an active brand. Brand-scoped routes
   * (Queue, Products, Credentials, run detail/report) require this — a principal
   * with no active brand (a super-admin with no membership selected) is routed
   * to the super-admin home instead of issuing a brand-scoped request that 403s.
   */
  hasActiveBrand: boolean;
  /** Convenience: signed in but no brand (a super-admin with no membership). */
  needsBrand: boolean;
  /** Global super-admin (from the `is_super_admin` token claim or /me). */
  isSuperAdmin: boolean;

  login: (email: string, password: string, brandId?: string) => Promise<void>;
  switchBrand: (brandId: string) => Promise<void>;
  /**
   * Super-admin: provision a brand + its first admin. Returns the created
   * brand/admin; the caller's own session is unchanged.
   */
  createBrand: (args: {
    name: string;
    admin_email: string;
    admin_password: string;
  }) => Promise<CreateBrandResponse>;
  /** Active-brand admin: provision a user in the current brand. */
  createUser: (args: ProvisionUserRequest) => Promise<void>;
  /**
   * Change the caller's own password. `currentPassword` is always required.
   * The backend re-mints a fresh token on success.
   */
  changePassword: (args: {
    currentPassword: string;
    newPassword: string;
  }) => Promise<void>;
  logout: () => void;

  /** Role-affordance predicate bound to the active role. */
  can: (capability: Capability) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [token, setTokenState] = useState<string | null>(() => getToken());
  const [user, setUser] = useState<UserResponse | null>(null);
  const [memberships, setMemberships] = useState<MembershipResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(() => getToken() !== null);

  // Keep React state in lockstep with the imperative token store (covers the
  // 401-clear path inside the API middleware and cross-tab logout).
  useEffect(() => subscribe((t) => setTokenState(t)), []);

  // Whenever the token changes, refresh identity. No token → cleared identity.
  useEffect(() => {
    let cancelled = false;
    if (!token) {
      setUser(null);
      setMemberships([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    void (async () => {
      try {
        const [me, mine] = await Promise.all([getMe(), getMyMemberships()]);
        if (cancelled) return;
        setUser(me);
        setMemberships(mine);
      } catch {
        // /me failed (likely 401 → token already cleared by middleware).
        if (!cancelled) {
          setUser(null);
          setMemberships([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const claims = useMemo(() => decodeToken(token), [token]);

  const login = useCallback(
    async (email: string, password: string, brandId?: string) => {
      const res = await apiLogin(email, password, brandId);
      setToken(res.access_token);
    },
    [],
  );

  const switchBrand = useCallback(async (brandId: string) => {
    const res = await apiSwitchBrand(brandId);
    setToken(res.access_token);
  }, []);

  // Super-admin provisions a new brand + its first admin. This does NOT change
  // the caller's own session/token — it returns the provisioned principals.
  const createBrand = useCallback(
    async (args: {
      name: string;
      admin_email: string;
      admin_password: string;
    }) => apiCreateBrand(args),
    [],
  );

  // Active-brand admin provisions a user in the current brand. The caller's
  // session is unchanged.
  const createUser = useCallback(async (args: ProvisionUserRequest) => {
    await apiCreateUser(args);
  }, []);

  // Change own password (current_password always required) → re-mints a fresh token.
  const changePassword = useCallback(
    async (args: { currentPassword: string; newPassword: string }) => {
      const res = await apiChangePassword({
        current_password: args.currentPassword,
        new_password: args.newPassword,
      });
      setToken(res.access_token);
    },
    [],
  );

  const logout = useCallback(() => {
    clearToken();
  }, []);

  const role = claims.role;
  const can = useCallback((capability: Capability) => canFn(role, capability), [role]);

  const isSuperAdmin = claims.isSuperAdmin || Boolean(user?.is_super_admin);

  const value = useMemo<AuthContextValue>(
    () => ({
      token,
      loading,
      user,
      memberships,
      activeBrandId: claims.brandId,
      role,
      isAuthenticated: Boolean(token) && Boolean(claims.brandId),
      hasActiveBrand: Boolean(claims.brandId),
      needsBrand: Boolean(token) && !claims.brandId,
      isSuperAdmin,
      login,
      switchBrand,
      createBrand,
      createUser,
      changePassword,
      logout,
      can,
    }),
    [
      token,
      loading,
      user,
      memberships,
      claims.brandId,
      role,
      isSuperAdmin,
      login,
      switchBrand,
      createBrand,
      createUser,
      changePassword,
      logout,
      can,
    ],
  );

  return <AuthContext value={value}>{children}</AuthContext>;
}

/** Access the auth session. Throws if used outside <AuthProvider>. */
export function useAuth(): AuthContextValue {
  const ctx = use(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within <AuthProvider>");
  return ctx;
}
